import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { UnmountClosed } from 'react-collapse';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './CollapsibleItem.themes';
type Props = {
  additionalClass?: string;
  children: ReactNode;
  title: string;
  subContent?: ReactNode;
  contentClassName?: string;
  // It's possible to pass undefined to this component (which will not render it), but undefined
  // is not a renderable type (i.e. a React.Node)
  details: ReactNode | null | undefined;
  theme?: keyof typeof themes;
};
export function CollapsibleItem({
  additionalClass,
  title,
  children,
  details,
  subContent,
  contentClassName = 'text-16',
  theme = 'default',
}: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const themeStyles = themes[theme];
  const {
    thItem,
    thContent,
    thContentNoDetails,
    thContentOpen,
    thIconContainer,
    thIconSize,
    thIcon,
    thSubContent,
    thDetails,
  } = themeStyles;
  const thIndicator = 'thIndicator' in themeStyles ? themeStyles.thIndicator : undefined;
  const thIndicatorOpen =
    'thIndicatorOpen' in themeStyles ? themeStyles.thIndicatorOpen : undefined;
  return (
    <div className={classnames(thItem, additionalClass)}>
      <div
        className={classnames(
          thIndicator,
          {
            [thIndicatorOpen]: showDetails,
          },
          'bottom-[15%] top-[15%]',
        )}
      />
      <div
        className={classnames(thContent, contentClassName, {
          [thContentNoDetails]: !details,
          [thContentOpen]: showDetails,
        })}
      >
        {children}
        {details && (
          <button
            aria-expanded={showDetails}
            type="button"
            className={thIconContainer}
            style={{
              width: thIconSize,
              height: thIconSize,
            }}
            onClick={() => setShowDetails(!showDetails)}
          >
            <Icon
              name={showDetails ? ICONS.CROSS_OUTLINE : ICONS.INFORMATION}
              width={thIconSize}
              className={thIcon}
            />
            <span className="sr-only">
              {`${title} - ${showDetails ? t('common.loadLess') : t('common.loadMore')}`}
            </span>
          </button>
        )}
      </div>
      {subContent && <div className={thSubContent}>{subContent}</div>}
      <UnmountClosed isOpened={showDetails}>
        <div className={classnames(thDetails, contentClassName)}>{details}</div>
      </UnmountClosed>
    </div>
  );
}
